import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "How ApexPath collects, uses and protects your personal information.",
    body: [
      ["Our commitment", "ApexPath is committed to protecting your privacy and handling your personal information responsibly and in line with applicable UK data protection law."],
      ["Information we collect", "We collect the information you provide when you contact us or request a consultation, such as your name, contact details and a summary of your circumstances. We do not ask for passports or sensitive documents at the initial enquiry stage."],
      ["How we use your information", "We use your information solely to respond to your enquiry, provide our services and communicate with you. We do not sell your information."],
      ["Data retention", "We retain personal information only for as long as necessary to provide our services and meet our legal obligations."],
      ["Your rights", "You have rights over your personal data, including the right to access, correct or request deletion of your information. To exercise these rights, please contact us."],
      ["Placeholder note", "This is a general template. ApexPath-specific final data protection registration details will be added here once confirmed."],
    ],
  },
  cookies: {
    title: "Cookies Policy",
    intro: "How this website uses cookies and similar technologies.",
    body: [
      ["What are cookies", "Cookies are small text files placed on your device to help a website function and to understand how it is used."],
      ["How we use cookies", "We use essential cookies to make the website work. We may use limited analytics to improve the site. We do not use cookies to identify you personally without consent."],
      ["Managing cookies", "You can control and delete cookies through your browser settings. Disabling some cookies may affect how the website works."],
      ["Placeholder note", "Final cookie and consent management details will be confirmed here."],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    intro: "The terms that apply to the use of this website.",
    body: [
      ["Use of this website", "The content on this website is provided for general information only and does not constitute legal or immigration advice. Advice is provided only through a formal client relationship."],
      ["No guarantees", "We do not publish or imply success-rate claims. Outcomes depend on individual circumstances and decisions made by the relevant authorities."],
      ["Scope of services", "ApexPath provides immigration advice and services in accordance with its applicable IAA authorisation."],
      ["Placeholder note", "Full terms of business will be provided as part of any formal engagement."],
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    intro: "Our commitment to an accessible website for everyone.",
    body: [
      ["Our approach", "We aim to make this website accessible and easy to use, including on mobile devices, with readable text, clear contrast, keyboard navigation and descriptive image alternatives."],
      ["Ongoing improvement", "We continue to review and improve accessibility across the site."],
      ["Feedback", "If you experience any difficulty using this website, please contact us and we will do our best to help."],
    ],
  },
  complaints: {
    title: "Complaints Information",
    intro: "How to raise a concern about our service.",
    body: [
      ["Our approach", "We take any concerns seriously and aim to resolve them promptly, fairly and professionally."],
      ["How to complain", "If you are unhappy with any aspect of our service, please contact us with the details and we will acknowledge and investigate your concern."],
      ["Placeholder note", "Details of the external complaints procedure and regulator escalation route will be added here once ApexPath's formal regulatory registration is confirmed."],
    ],
  },
  regulatory: {
    title: "Regulatory Information",
    intro: "ApexPath's regulatory position.",
    body: [
      ["Our authorisation", "Immigration advice and services are provided in accordance with ApexPath's applicable IAA authorisation."],
      ["IAA registration", "Our Immigration Advice Authority (IAA) registration number and official digital verification badge will be displayed here once formal registration is confirmed."],
      ["No success claims", "We do not publish success-rate claims. All advice is tailored to individual circumstances."],
    ],
  },
};

export default function Legal() {
  const { page } = useParams();
  const data = CONTENT[page];
  if (!data) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-navy">Page not found</h1>
        <Link to="/" className="btn-gold mt-6">Back to Home</Link>
      </div>
    );
  }
  return (
    <>
      <Seo title={`${data.title} | ApexPath`} description={data.intro} />
      <PageHero crumbs={[{ label: "Home", to: "/" }, { label: data.title }]} eyebrow="Legal & Regulatory" title={data.title} subtitle={data.intro} />
      <section className="bg-white py-16">
        <div className="container-x max-w-3xl">
          {data.body.map(([h, t]) => (
            <div key={h} className="mb-8">
              <h2 className="font-serif text-2xl font-semibold text-navy">{h}</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{t}</p>
            </div>
          ))}
          <p className="rounded-lg border-l-4 border-gold bg-cream p-4 text-xs italic text-slate-500">
            This page contains placeholder content where ApexPath-specific final details have not yet been supplied.
          </p>
        </div>
      </section>
    </>
  );
}
